import { ActionType, TenantPartnershipId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureInput, inferProcedureOutput } from "@trpc/server";
import { EmptyCard } from "components/dashboard/card";
import { PipelineColumnHeader } from "components/dashboard/column";
import { HomeCareLeadCard } from "components/dashboard/home-care-lead-card";
import { pipelineColumnTitle } from "components/dashboard/pipeline-column-title";
import { pipelineStatusDate } from "components/dashboard/pipeline-status-date";
import { trpc } from "components/rpc/trpc";
import { Badge } from "components/ui/badge";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useIsAllowed, useRegionId } from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import { handleEvent } from "util/user-events";
import { isNoLongerSharedError } from "./shared-lead-load-error";
import { homeCarePipelinePath, sharedLeadPath } from "./shared-pipeline-tabs";
import { SharedPipeline, SharedPipelineStatus } from "./use-shared-pipelines";

type SharedLeadsInput = Exclude<
    inferProcedureInput<AppRouter["sharedPipeline"]["leads"]>,
    void
>;
type SharedLead = inferProcedureOutput<AppRouter["sharedPipeline"]["leads"]>[number];
type SharedLeadSearch = Pick<SharedLeadsInput, "searchString" | "searchType" | "sort">;

const useSharedLeadSearch = (): SharedLeadSearch => {
    const searchText = useSelector(
        (state: ApplicationState) => state.clientSearch.clientSearchInstance.searchText,
    );
    const searchType = useSelector(
        (state: ApplicationState) => state.clientSearch.clientSearchInstance.searchType,
    );
    const cardSort = useSelector(
        (state: ApplicationState) =>
            state.clientSearch.clientSearchInstance.cardSortPreference,
    );
    return React.useMemo(
        () => ({
            searchString: searchText,
            searchType,
            sort: cardSort,
        }),
        [searchText, searchType, cardSort],
    );
};

const SharedLeadCard: React.FC<{
    lead: SharedLead;
    status: SharedPipelineStatus;
    partnershipId: TenantPartnershipId;
}> = ({ lead, status, partnershipId }) => {
    const canViewLead = useIsAllowed(ActionType.ViewSharedHomeCareLead);
    return (
        <HomeCareLeadCard
            lead={{
                href: canViewLead ? sharedLeadPath(partnershipId, lead.id) : null,
                name: lead.name,
                bestContactName: lead.bestContactName,
                nextTask: lead.nextTask,
                assignedUserName: lead.assignedUserName,
                status: pipelineStatusDate(status, lead),
            }}
            highlightCard={false}
            pendingCard={false}
            showAssignedTo={true}
        />
    );
};

const SharedColumnBadge: React.FC<{
    query: { data: SharedLead[] | undefined; isError: boolean };
}> = ({ query }) => {
    if (query.data) {
        return <Badge type="info" value={query.data.length} />;
    }
    return query.isError ? null : <Spinner />;
};

const useRefreshSharedPipelinesWhenUnshared = (
    error: unknown,
    regionId: ReturnType<typeof useRegionId>,
): void => {
    const trpcContext = trpc.useTRPC();
    const queryClient = useQueryClient();
    const noLongerShared = isNoLongerSharedError(error);
    React.useEffect(() => {
        if (!noLongerShared) {
            return;
        }
        queryClient.invalidateQueries({
            queryKey: trpcContext.sharedPipeline.list.queryOptions({ regionId }).queryKey,
        });
    }, [noLongerShared, queryClient, trpcContext, regionId]);
};

const SharedPipelineColumn: React.FC<{
    pipeline: SharedPipeline;
    status: SharedPipelineStatus;
    search: SharedLeadSearch;
}> = ({ pipeline, status, search }) => {
    const regionId = useRegionId();
    const trpcContext = trpc.useTRPC();
    const query = useQuery({
        ...trpcContext.sharedPipeline.leads.queryOptions({
            regionId,
            partnershipId: pipeline.partnershipId,
            statusListItemId: status.id,
            ...search,
        }),
        enabled: !!regionId,
        retry: false,
    });
    useRefreshSharedPipelinesWhenUnshared(query.error, regionId);
    const title = pipelineColumnTitle(status.name, status.categoryKey);
    const leads = query.data;

    return (
        <div className={`col pipeline-stage pipeline-stage-${status.key}`}>
            <div className="pipeline-innards">
                <PipelineColumnHeader
                    icon={status.icon}
                    title={title}
                    badge={<SharedColumnBadge query={query} />}
                />
                {query.isError ? (
                    <div className="pipeline-content">
                        <InlineBanner type="error">
                            These clients could not be loaded. Try again in a moment.
                        </InlineBanner>
                    </div>
                ) : null}
                {leads ? (
                    <div className="pipeline-content">
                        {leads.length > 0 ? (
                            leads.map(lead => (
                                <SharedLeadCard
                                    key={lead.id}
                                    lead={lead}
                                    status={status}
                                    partnershipId={pipeline.partnershipId}
                                />
                            ))
                        ) : (
                            <EmptyCard />
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export const SharedPipelineBoard: React.FC<{ pipeline: SharedPipeline }> = ({
    pipeline,
}) => {
    const search = useSharedLeadSearch();

    React.useEffect(() => {
        handleEvent("shared-pipeline-open", { partnershipId: pipeline.partnershipId });
    }, [pipeline.partnershipId]);

    return (
        <div className="pipeline-wrapper bg-brand-700 flex px-4 py-2 flex-1 h-screen md:h-auto md:overflow-hidden">
            <div className="pipeline row main-body">
                {pipeline.statuses
                    .filter(status => status.showOnPipeline)
                    .map(status => (
                        <SharedPipelineColumn
                            key={status.id}
                            pipeline={pipeline}
                            status={status}
                            search={search}
                        />
                    ))}
            </div>
        </div>
    );
};

export const SharedPipelineLoadError: React.FC = () => (
    <div className="p-4">
        <InlineBanner type="error">
            Shared pipelines could not be loaded. Try again in a moment.{" "}
            <Link to={homeCarePipelinePath} className={defaultLinkStyle}>
                Back to your pipeline
            </Link>
        </InlineBanner>
    </div>
);

export const SharedPipelineUnavailable: React.FC = () => (
    <div className="p-4">
        <InlineBanner type="warning">
            This shared pipeline is no longer available. The partner agency may have
            stopped sharing it.{" "}
            <Link to={homeCarePipelinePath} className={defaultLinkStyle}>
                Back to your pipeline
            </Link>
        </InlineBanner>
    </div>
);
